import zapierLogoPath from '../../../assets/icons/zapier-logo.svg';
import makeLogoPath from '../../../assets/icons/make-logo.svg';

export type WebhookProvider =
  | 'zapier'
  | 'make'
  | 'slack'
  | 'hubspot'
  | 'salesforce'
  | 'salesloft'
  | 'calendar'
  | 'notion'
  | 'jira'
  | 'miro'
  | 'trello'
  | 'clickup'
  | 'greenhouse'
  | 'google-sheets'
  | 'airtable'
  | 'monday'
  | 'zoho'
  | 'pipedrive'
  | 'confluence'
  | 'default';

export interface Webhook {
  url: string;
  name: string;
}

export interface Integrations {
  webhooks: Webhook[];
}

interface WebhookProviderConfig {
  id: WebhookProvider;
  name: string;
  domains: string[];
  logoPath?: string;
  howToDirections?: string[];
}

export const WEBHOOK_PROVIDERS: WebhookProviderConfig[] = [
  {
    id: 'zapier',
    name: 'Zapier',
    domains: ['zapier.com', 'hooks.zapier.com'],
    logoPath: zapierLogoPath,
    howToDirections: [
      '1. Log in to your Zapier account',
      '2. Click "Create Zap"',
      '3. Choose "Webhook by Zapier" as your trigger',
      '4. Select "Catch Hook" as the trigger event',
      '5. Copy the provided webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'make',
    name: 'Make',
    domains: ['make.com', 'integromat.com'],
    logoPath: makeLogoPath,
    howToDirections: [
      '1. Log in to Make (formerly Integromat)',
      '2. Create a new scenario',
      '3. Add a webhook trigger module',
      '4. Select "Custom webhook"',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'slack',
    name: 'Slack',
    domains: ['slack.com'],
    howToDirections: [
      '1. Go to your Slack workspace settings',
      '2. Navigate to Incoming Webhooks',
      '3. Click "Add to Slack"',
      '4. Choose the channel for notifications',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    domains: ['hubspot.com'],
    howToDirections: [
      '1. Log in to HubSpot',
      '2. Go to Settings > Integrations > Webhooks',
      '3. Click "Create webhook"',
      '4. Configure the webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    domains: ['salesforce.com'],
    howToDirections: [
      '1. Log in to Salesforce',
      '2. Set up an Apex REST endpoint',
      '3. Create a new Site for external access',
      '4. Configure the endpoint URL',
      '5. Copy the complete webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'salesloft',
    name: 'SalesLoft',
    domains: ['salesloft.com'],
    howToDirections: [
      '1. Access your SalesLoft account',
      '2. Go to Team Settings > Integrations',
      '3. Set up a custom webhook endpoint',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'calendar',
    name: 'Google Calendar',
    domains: ['calendar.google.com'],
    howToDirections: [
      '1. Set up a Google Cloud Project',
      '2. Enable Google Calendar API',
      '3. Create push notification channel',
      '4. Configure webhook endpoint',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'notion',
    name: 'Notion',
    domains: ['notion.so'],
    howToDirections: [
      '1. Create a Notion integration',
      '2. Set up API access',
      '3. Configure webhook endpoint',
      '4. Set up notification settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'jira',
    name: 'Jira',
    domains: ['atlassian.net'],
    howToDirections: [
      '1. Go to Jira Settings',
      '2. Navigate to System > WebHooks',
      '3. Click "Create WebHook"',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'miro',
    name: 'Miro',
    domains: ['miro.com'],
    howToDirections: [
      '1. Go to your Miro team settings',
      '2. Navigate to the Apps & Integrations section',
      '3. Set up webhook integration',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'trello',
    name: 'Trello',
    domains: ['trello.com'],
    howToDirections: [
      '1. Go to Trello Power-Ups',
      '2. Add Custom Fields Power-Up',
      '3. Configure webhook settings',
      '4. Set up notification preferences',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'clickup',
    name: 'ClickUp',
    domains: ['clickup.com'],
    howToDirections: [
      '1. Access ClickUp Settings',
      '2. Go to Integrations',
      '3. Set up webhook integration',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'greenhouse',
    name: 'Greenhouse',
    domains: ['greenhouse.io'],
    howToDirections: [
      '1. Log in to Greenhouse',
      '2. Go to Dev Center',
      '3. Configure webhook endpoint',
      '4. Set up webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    domains: ['sheets.googleapis.com'],
    howToDirections: [
      '1. Set up Google Cloud Project',
      '2. Enable Google Sheets API',
      '3. Create Apps Script webhook',
      '4. Deploy web app',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'airtable',
    name: 'Airtable',
    domains: ['airtable.com'],
    howToDirections: [
      '1. Open your Airtable base',
      '2. Go to Automations',
      '3. Create webhook automation',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'monday',
    name: 'Monday.com',
    domains: ['monday.com'],
    howToDirections: [
      '1. Access Monday.com settings',
      '2. Go to Integrations',
      '3. Set up webhook integration',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'zoho',
    name: 'Zoho',
    domains: ['zoho.com'],
    howToDirections: [
      '1. Log in to Zoho',
      '2. Access Integration Settings',
      '3. Set up webhook endpoint',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    domains: ['pipedrive.com'],
    howToDirections: [
      '1. Go to Pipedrive Settings',
      '2. Navigate to Webhooks',
      '3. Click Add Webhook',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'confluence',
    name: 'Confluence',
    domains: ['confluence.atlassian.net'],
    howToDirections: [
      '1. Access Confluence Settings',
      '2. Go to Integration settings',
      '3. Set up webhook integration',
      '4. Configure webhook settings',
      '5. Copy the webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
  {
    id: 'default',
    name: 'Custom Webhook',
    domains: [],
    howToDirections: [
      '1. Set up your webhook endpoint',
      '2. Ensure it accepts POST requests',
      '3. Configure authentication if needed',
      '4. Test the endpoint',
      '5. Copy your webhook URL',
      '6. Paste the URL here in the webhook configuration',
    ],
  },
];

export const getWebhookType = (url: string): WebhookProvider => {
  const foundProvider = WEBHOOK_PROVIDERS.find((p) =>
    p.domains.some((domain) => url.includes(domain))
  );
  return foundProvider?.id || 'default';
};
